import { Shop } from "@/types/v2";
import { useMemo } from "react";
import { TagsRepository } from "@/repositories/tags";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { TagList } from "@/components/Tags/TagList";
import { Tag, TagsDocument } from "@/types/v2/Tags";
import { useStore } from "@/stores/useStore";
import { AddTagDialog } from "@/components/Tags/AddTagDialog";

type TagSettingsProps = {
  shopData: Shop;
  syncWithSource: () => void;
};

export const TagSettings = ({ shopData, syncWithSource }: TagSettingsProps) => {
  const shopId = useMemo(() => shopData.id, [shopData]);
  const tagsDoc = useStore((s) => s.tags);
  const tags = useMemo(() => tagsDoc?.tags ?? [], [tagsDoc]);

  const handleSaveTag = async (tag: Tag) => {
    if (!shopId || !tagsDoc) return;

    const updatedTags = tagsDoc.tags.map((t) => (t.id === tag.id ? tag : t));

    const updatedTagsDoc: TagsDocument = {
      ...tagsDoc,
      tags: updatedTags,
    };

    try {
      await TagsRepository.update(shopId, updatedTagsDoc);
      showSuccessToast("Tag Updated", "The tag has been updated successfully.");
      syncWithSource();
    } catch (err) {
      console.error("An error occurred updating tag: ", err);
      showErrorToast(
        "Tag Failed to Update",
        `An error occurred updating tag: ${err}`
      );
    }
  };

  const handleDeleteTag = async (tag: Tag) => {
    if (!shopId || !tagsDoc) return;

    try {
      await TagsRepository.removeTag(shopId, tag, tagsDoc);
      showSuccessToast("Tag Deleted", "The tag has been deleted successfully.");
      syncWithSource();
    } catch (err) {
      console.error("An error occurred deleting tag: ", err);
      showErrorToast(
        "Tag Failed to Delete",
        `An error occurred deleting tag: ${err}`
      );
    }
  };

  const handleAddTag = async (tag: Tag) => {
    if (!shopId) return;

    // Check if tag with same label already exists
    const tagExists =
      tagsDoc?.tags.some(
        (t) => t.label.toLowerCase() === tag.label.toLowerCase()
      ) ?? false;

    if (tagExists) {
      showErrorToast(
        "Tag Already Exists",
        "A tag with this label already exists."
      );
      return;
    }

    try {
      if (!tagsDoc) {
        await TagsRepository.create(shopId, [tag]);
      } else {
        const updatedTags = [...tagsDoc.tags, tag];
        const updatedTagsDoc: TagsDocument = {
          ...tagsDoc,
          tags: updatedTags,
        };

        await TagsRepository.update(shopId, updatedTagsDoc);
      }
      showSuccessToast("Tag Added", "The tag has been added successfully.");
      syncWithSource();
    } catch (err) {
      console.error("An error occurred adding tag: ", err);
      showErrorToast(
        "Tag Failed to Add",
        `An error occurred adding tag: ${err}`
      );
    }
  };

  const handleReorderTags = async (orderedIds: string[]) => {
    if (!shopId || !tagsDoc) return;

    try {
      await TagsRepository.reorderTags(shopId, orderedIds, tagsDoc);
      syncWithSource();
    } catch (err) {
      console.error("An error occurred reordering tags: ", err);
      showErrorToast(
        "Tags Failed to Reorder",
        `An error occurred reordering tags: ${err}`
      );
    }
  };

  return (
    <div className="flex max-w-96 flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Manage Tags</h2>
          <p className="text-sm text-muted-foreground">
            Edit, delete, or drag to reorder tags used in your cards. This order
            is how tags appear on every card.
          </p>
        </div>
        <AddTagDialog onAdd={handleAddTag} />
      </div>
      <TagList
        tags={tags}
        onEdit={handleSaveTag}
        onDelete={handleDeleteTag}
        onReorder={handleReorderTags}
      />
    </div>
  );
};
